if (!customElements.get('faqs-item')) {
    let faqsInstanceId = 0;

    class FAQs extends HTMLElement {
        constructor() {
            super();

            this.initialized = false;
            this.documentClickAttached = false;
            this.instanceId = this.id || `FaqsItem-${++faqsInstanceId}`;
            this.onDocumentClick = this.onDocumentClick.bind(this);
        }

        connectedCallback() {
            const initialize = () => {
                if (!this.isConnected) return;

                if (!this.initialized) {
                    this.initialize();
                } else {
                    this.attachDocumentClick();
                }
            };

            if (this.querySelector('.faqs')) {
                initialize();
            } else {
                Promise.resolve().then(initialize);
            }
        }

        disconnectedCallback() {
            this.detachDocumentClick();
            this.querySelectorAll('.collapse').forEach((panel) => this.clearPanelTransition(panel));
        }

        initialize() {
            if (this.initialized || !this.querySelector('.faqs')) return;

            this.initialized = true;
            this.initializeFilter();
            this.initializeAccordions();
        }

        initializeFilter() {
            this.filter = this.querySelector('[data-faqs-filter-root]');
            if (!this.filter) return;

            this.filterToggle = this.filter.querySelector('[data-faqs-filter]');
            this.filterDropdown = this.filter.querySelector('.faqs-filterDropdown-menu');
            this.filterDropdownArrow = this.filterToggle?.querySelector('[data-dropdown-arrow]');
            this.filterOptions = Array.from(this.filter.querySelectorAll('[data-faqs-filter-option]'));

            if (!this.filterToggle || !this.filterDropdown) return;

            this.filterToggle.addEventListener('click', () => {
                this.setFilterOpen(this.filterToggle.getAttribute('aria-expanded') !== 'true');
            });

            this.filterOptions.forEach((option) => {
                option.setAttribute('aria-pressed', option.closest('li')?.classList.contains('is-active') ? 'true' : 'false');
                option.addEventListener('click', (event) => this.onFilterOptionClick(event));
            });

            this.filter.addEventListener('keydown', (event) => {
                if (event.key !== 'Escape' || this.filterToggle.getAttribute('aria-expanded') !== 'true') return;

                event.preventDefault();
                event.stopPropagation();
                this.setFilterOpen(false, true);
            });

            this.setFilterOpen(false);
            this.attachDocumentClick();
        }

        attachDocumentClick() {
            if (!this.filterDropdown || this.documentClickAttached) return;

            document.addEventListener('click', this.onDocumentClick);
            this.documentClickAttached = true;
        }

        detachDocumentClick() {
            if (!this.documentClickAttached) return;

            document.removeEventListener('click', this.onDocumentClick);
            this.documentClickAttached = false;
        }

        setFilterOpen(isOpen, returnFocus = false) {
            if (!this.filterToggle || !this.filterDropdown) return;

            this.filterToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            this.filterDropdown.classList.toggle('is-show', isOpen);
            this.filterDropdown.hidden = !isOpen;
            this.filterDropdownArrow?.classList.toggle('active', isOpen);

            if (!isOpen && returnFocus) this.filterToggle.focus();
        }

        onFilterOptionClick(event) {
            const option = event.currentTarget;
            const selectedItem = option.closest('li');
            if (!selectedItem) return;

            this.filterOptions.forEach((filterOption) => {
                const isSelected = filterOption === option;
                filterOption.closest('li')?.classList.toggle('is-active', isSelected);
                filterOption.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });

            const filterValue = option.getAttribute('data-value');
            this.filterToggle.querySelector('.text').textContent = option.textContent.trim();

            this.querySelectorAll('.faqs-paragraph').forEach((paragraph) => {
                const isVisible = filterValue === null || paragraph.id === filterValue;
                paragraph.classList.toggle('is-hidden', !isVisible);
                paragraph.classList.toggle('is-active', filterValue !== null && isVisible);
            });

            this.setFilterOpen(false, true);
        }

        onDocumentClick(event) {
            if (!this.filter || this.filter.contains(event.target)) return;

            this.setFilterOpen(false);
        }

        initializeAccordions() {
            this.querySelectorAll('.card-header').forEach((trigger, index) => {
                if (trigger.dataset.faqInitialized === 'true') return;

                const panel = this.getControlledPanel(trigger);
                if (!panel) return;

                trigger.dataset.faqInitialized = 'true';
                this.normalizeLegacyTrigger(trigger);

                if (!trigger.id) trigger.id = `${this.instanceId}-Question-${index + 1}`;
                if (!panel.id) panel.id = `${this.instanceId}-Panel-${index + 1}`;

                trigger.setAttribute('aria-controls', panel.id);
                panel.setAttribute('aria-labelledby', trigger.id);

                const isExpanded = !trigger.classList.contains('collapsed') && trigger.getAttribute('aria-expanded') !== 'false';
                this.setInitialPanelState(trigger, panel, isExpanded);

                trigger.addEventListener('click', () => this.togglePanel(trigger, panel));

                if (trigger.tagName !== 'BUTTON') {
                    trigger.addEventListener('keydown', (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;

                        event.preventDefault();
                        trigger.click();
                    });
                }
            });
        }

        normalizeLegacyTrigger(trigger) {
            if (trigger.tagName === 'BUTTON') return;

            trigger.setAttribute('role', 'button');
            trigger.setAttribute('tabindex', '0');

            const nestedTrigger = trigger.querySelector('.title[role="button"], .title[tabindex]');
            if (nestedTrigger) {
                nestedTrigger.removeAttribute('role');
                nestedTrigger.removeAttribute('tabindex');
            }
        }

        getControlledPanel(trigger) {
            const controlledId = trigger.getAttribute('aria-controls');
            if (controlledId) {
                const controlledPanel = Array.from(this.querySelectorAll('.collapse')).find((panel) => panel.id === controlledId);
                if (controlledPanel) return controlledPanel;
            }

            return trigger.nextElementSibling?.classList.contains('collapse') ? trigger.nextElementSibling : null;
        }

        setInitialPanelState(trigger, panel, isExpanded) {
            this.clearPanelTransition(panel);
            trigger.classList.toggle('collapsed', !isExpanded);
            trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
            panel.hidden = !isExpanded;
            panel.toggleAttribute('inert', !isExpanded);
            panel.style.maxHeight = isExpanded ? 'none' : '0px';
        }

        togglePanel(trigger, panel) {
            const shouldExpand = trigger.getAttribute('aria-expanded') !== 'true';
            this.clearPanelTransition(panel);

            trigger.classList.toggle('collapsed', !shouldExpand);
            trigger.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');

            if (shouldExpand) {
                this.openPanel(trigger, panel);
            } else {
                this.closePanel(trigger, panel);
            }
        }

        openPanel(trigger, panel) {
            panel.hidden = false;
            panel.removeAttribute('inert');

            if (this.prefersReducedMotion()) {
                panel.style.maxHeight = 'none';
                return;
            }

            panel.style.maxHeight = '0px';
            void panel.offsetHeight;

            this.nextFrame(() => {
                if (trigger.getAttribute('aria-expanded') !== 'true') return;

                panel.style.maxHeight = `${panel.scrollHeight}px`;
                this.afterPanelTransition(panel, () => {
                    if (trigger.getAttribute('aria-expanded') === 'true') panel.style.maxHeight = 'none';
                });
            });
        }

        closePanel(trigger, panel) {
            if (panel.contains(document.activeElement)) trigger.focus();
            panel.setAttribute('inert', '');

            const finishClose = () => {
                if (trigger.getAttribute('aria-expanded') === 'true') return;

                panel.hidden = true;
                panel.style.maxHeight = '0px';
            };

            if (this.prefersReducedMotion() || panel.hidden) {
                finishClose();
                return;
            }

            const currentHeight = panel.getBoundingClientRect().height || panel.scrollHeight;
            panel.style.maxHeight = `${currentHeight}px`;
            void panel.offsetHeight;

            this.nextFrame(() => {
                if (trigger.getAttribute('aria-expanded') === 'true') return;

                panel.style.maxHeight = '0px';
                this.afterPanelTransition(panel, finishClose);
            });
        }

        nextFrame(callback) {
            if (window.requestAnimationFrame) {
                window.requestAnimationFrame(callback);
            } else {
                window.setTimeout(callback, 0);
            }
        }

        afterPanelTransition(panel, callback) {
            this.clearPanelTransition(panel);

            const finish = (event) => {
                if (event && (event.target !== panel || event.propertyName !== 'max-height')) return;

                this.clearPanelTransition(panel);
                callback();
            };

            panel.faqTransitionHandler = finish;
            panel.addEventListener('transitionend', finish);
            panel.faqTransitionTimer = window.setTimeout(() => finish(), 550);
        }

        clearPanelTransition(panel) {
            if (panel.faqTransitionHandler) {
                panel.removeEventListener('transitionend', panel.faqTransitionHandler);
                panel.faqTransitionHandler = null;
            }

            if (panel.faqTransitionTimer) {
                window.clearTimeout(panel.faqTransitionTimer);
                panel.faqTransitionTimer = null;
            }
        }

        prefersReducedMotion() {
            return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
        }
    }

    customElements.define('faqs-item', FAQs);
}
